import api from './api';

export interface DiagnosticConfig {
  default_fee: number;
  fees_by_location: Record<string, number>;
}

export interface Location {
  name: string;
  code: string;
  active: boolean;
}

export interface EmailTemplate {
  subject: string;
  body: string;
}

export interface EmailTemplates {
  intervention_created: EmailTemplate;
  intervention_assigned: EmailTemplate;
  payment_received: EmailTemplate;
  subscription_expiring: EmailTemplate;
  [key: string]: EmailTemplate;
}

class SettingsService {
  // Récupérer tous les paramètres
  async getAllSettings(): Promise<Record<string, any>> {
    try {
      const response = await api.get('/admin/settings');
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des paramètres:', error);
      throw error;
    }
  }

  // Récupérer les paramètres d'une catégorie
  async getSettingsByCategory(category: string): Promise<Record<string, any>> {
    try {
      const response = await api.get(`/admin/settings/${category}`);
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des paramètres:', error);
      throw error;
    }
  }

  // Mettre à jour plusieurs paramètres
  async updateSettings(settings: Record<string, any>): Promise<void> {
    try {
      await api.put('/admin/settings', { settings });
    } catch (error) {
      console.error('Erreur lors de la mise à jour des paramètres:', error);
      throw error;
    }
  }

  // Configuration des frais de diagnostic
  async getDiagnosticConfig(): Promise<DiagnosticConfig> {
    try {
      const response = await api.get('/admin/settings/diagnostic');
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération config diagnostic:', error);
      throw error;
    }
  }

  async updateDiagnosticConfig(config: Partial<DiagnosticConfig>): Promise<void> {
    try {
      await api.put('/admin/settings/diagnostic', config);
    } catch (error) {
      console.error('Erreur lors de la mise à jour config diagnostic:', error);
      throw error;
    }
  }

  // Gestion des zones de service
  async getLocations(): Promise<Location[]> {
    try {
      const response = await api.get('/admin/settings/locations');
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des zones:', error);
      throw error;
    }
  }

  async updateLocations(locations: Location[]): Promise<void> {
    try {
      await api.put('/admin/settings/locations', { locations });
    } catch (error) {
      console.error('Erreur lors de la mise à jour des zones:', error);
      throw error;
    }
  }

  // Templates d'email
  async getEmailTemplates(): Promise<EmailTemplates> {
    try {
      const response = await api.get('/admin/settings/email-templates');
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des templates:', error);
      throw error;
    }
  }

  async updateEmailTemplates(templates: Partial<EmailTemplates>): Promise<void> {
    try {
      await api.put('/admin/settings/email-templates', { templates });
    } catch (error) {
      console.error('Erreur lors de la mise à jour des templates:', error);
      throw error;
    }
  }

  // Versions des applications mobiles (iOS & Android)
  async getAppVersionConfig(): Promise<AppVersionSettings> {
    try {
      const response = await api.get('/admin/settings/app-version');
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération de la configuration des versions mobiles:', error);
      throw error;
    }
  }

  async updateAppVersionConfig(payload: { ios?: Partial<AppPlatformConfig>; android?: Partial<AppPlatformConfig> }): Promise<AppVersionSettings> {
    try {
      const response = await api.put('/admin/settings/app-version', payload);
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des versions mobiles:', error);
      throw error;
    }
  }
}

export interface AppPlatformConfig {
  latest_version: string;
  latest_build: number;
  min_version: string;
  min_build: number;
  force_update: boolean;
  title: string;
  message: string;
  store_url: string;
}

export interface AppVersionSettings {
  ios: AppPlatformConfig;
  android: AppPlatformConfig;
}

export const settingsService = new SettingsService();

